import translate from '@translate';
import React, { FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommuteIcon from '@mui/icons-material/Commute';
import FormBlock from 'src/components/FormBlock';
import Button from 'src/components/Button';
import StatusLabel from 'src/components/StatusLabel';
import TableSimple from 'src/components/TableSimple';
import Typography from 'src/components/Typography';
import { sizes } from 'src/themes/default';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { OrderTransportLinkType } from 'src/types/orderDetail';
import { OrderType } from 'src/types/order';
import { RootStoreType } from 'src/types/store';
import { TableColumnType, TableRowType } from 'src/types/table';
import { getOrderPalletsFetch, getPalletTypesFetch } from 'src/resources/orderDetail/reducer';
import { orderTransportsAllColumns } from './constants';
import { orderToManualTransportPrefill } from './orderToManualTransportPrefill';
import { Stack } from '@mui/material';
import { useArchiveMode } from 'src/utils/archiveMode';
import { withArchive } from 'src/resources/withArchive';

export interface OrderTransportsTablePropsType {
    data?: OrderTransportLinkType[] | null;
    orderData: OrderType | null;
    transportStatusCodesObject: ConstantsCodesObjectType;
    shipperCodesObject: ConstantsCodesObjectType;
    canCreateTransport: boolean;
}

interface OrderTransportRowType extends OrderTransportLinkType {
    statusLabel?: string;
    statusColor?: string;
    shipperLabel?: string;
    roleLabel?: string;
}

const roleTranslateKey = (role: string | undefined): string => {
    if (role === 'system') {
        return translate('orderDetail.transportsTable.roleSystem');
    }
    if (role === 'manual') {
        return translate('orderDetail.transportsTable.roleManual');
    }
    return role ?? '';
};

const OrderTransportsTable: FC<OrderTransportsTablePropsType> = (props) => {
    const navigate = useNavigate();
    const archive = useArchiveMode();
    const dispatch = useDispatch();
    const orderPallets = useSelector((state: RootStoreType) => state.orderDetail.orderPallets);
    const palletTypes = useSelector((state: RootStoreType) => state.orderDetail.palletTypes);
    const palletsPrefetchOrderIdRef = useRef<number | null>(null);

    useEffect(() => {
        const orderId = props.orderData?.ID;
        if (archive || !orderId || palletsPrefetchOrderIdRef.current === orderId) {
            return;
        }
        palletsPrefetchOrderIdRef.current = orderId;
        if (!orderPallets) {
            dispatch(getOrderPalletsFetch(String(orderId)));
        }
        if (!palletTypes) {
            dispatch(getPalletTypesFetch(String(orderId)));
        }
    }, [dispatch, archive, orderPallets, palletTypes, props.orderData?.ID]);

    const rows = useMemo((): OrderTransportRowType[] => {
        if (!props.data?.length) {
            return [];
        }
        return props.data.map((item) => {
            const statusOption = props.transportStatusCodesObject[item.STATUS || ''];
            const shipperOption = props.shipperCodesObject[item.SHIPPER || ''];
            return {
                ...item,
                STATUS: statusOption?.label ?? item.STATUS,
                statusColor: statusOption?.color,
                statusLabel: statusOption?.label ?? item.STATUS,
                shipperLabel: shipperOption?.label ?? item.SHIPPER,
                SHIPPER: shipperOption?.label ?? item.SHIPPER,
                roleLabel: roleTranslateKey(item.ROLE),
                ROLE: roleTranslateKey(item.ROLE),
            };
        });
    }, [props.data, props.transportStatusCodesObject, props.shipperCodesObject]);

    const customRender = (column: TableColumnType, row: TableRowType): ReactNode => {
        const transportRow = row as OrderTransportRowType;
        if (column.key === 'STATUS') {
            return <StatusLabel label={transportRow.statusLabel} color={transportRow.statusColor} />;
        }
        return row[column.key];
    };

    const handleCreateTransport = () => {
        if (!props.orderData?.ID) {
            return;
        }
        const prefill = orderToManualTransportPrefill(props.orderData, undefined, {
            pallets: orderPallets ?? props.orderData.ORDER_PALLETS,
            palletTypes,
        });
        navigate('/transports/new', {
            state: {
                fromOrder: {
                    orderId: props.orderData.ID,
                    prefill,
                },
            },
        });
    };

    return (
        <div data-testid="order-transports-tab">
            {props.canCreateTransport && (
                <FormBlock sx={{ marginBottom: sizes.blocks_top_MG }}>
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<CommuteIcon />}
                            onClick={handleCreateTransport}
                            data-testid="order-create-transport-btn"
                        >
                            {translate('orderDetail.action.createTransport')}
                        </Button>
                    </Stack>
                </FormBlock>
            )}
            {!rows.length ? (
                <Typography variant="h2" align="center" sx={{ marginTop: sizes.blocks_top_MG }}>
                    {translate('orderDetail.emptyData')}
                </Typography>
            ) : (
                <TableSimple
                    rowIdKey="ID"
                    rows={rows}
                    columsLabelPrefix="orderDetail.transportsTable"
                    allColumns={orderTransportsAllColumns}
                    initSort={{ column: 'ID', direction: 'DESC' }}
                    customRender={customRender}
                    rowActions={[
                        {
                            icon: <CommuteIcon />,
                            label: translate('transports.action.transportDetail'),
                            getLink: (row) => withArchive(`/transports/${row.ID}/`, archive),
                        },
                    ]}
                    heightVariant="top-tab"
                />
            )}
        </div>
    );
};

export default OrderTransportsTable;
